import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
  PayloadAction,
} from '@reduxjs/toolkit';
import {
  createLessonTutorFeedback,
  getLessonTutorFeedbackRewards,
  getLessonTutorFeedbacks,
  updateLessonTutorFeedbacks,
} from './api';
import {
  adminCreateLessonTutorFeedback,
  adminDeleteLessonTutorFeedback,
  adminGetLessonTutorFeedbackRewards,
  adminGetLessonTutorFeedbacks,
  adminUpdateLessonTutorFeedbacks,
} from './adminApi';
import { RootState } from '../..';
import { ISection } from '../SectionSlice';
import { ILessonHistory } from '../LessonHistorySlice';
import { IUser } from '../../UserSlice';

export type TLessonTutorFeedbackRewardItem = {
  title: string;
  list: string[];
};

export interface ILessonTutorFeedbackRewardData {
  0: TLessonTutorFeedbackRewardItem;
  1: TLessonTutorFeedbackRewardItem;
  2: TLessonTutorFeedbackRewardItem;
  3: TLessonTutorFeedbackRewardItem;
  4: TLessonTutorFeedbackRewardItem;
  5: TLessonTutorFeedbackRewardItem;
}

export interface ILessonTutorFeedback {
  _id: string;
  tutor: string | IUser;
  student: string | IUser;
  section: string | ISection;
  lessonHistory: string | ILessonHistory;
  createdBy: string | IUser;
  lastUpdatedBy: string | IUser;
  createdAt: string;
  updatedAt: string;
  isStudentAttended: 'Yes' | 'No';
  lessonExtension?: number;
  lessonExtensionReason?: string;
  studentAttentiveness?: string;
  graspingAndApplicationOfConceptTaught?: string;
  progressForThisTopicAndSyllabus?: string;
  homeworkCompletedFromPreviousLesson?: string;
  homeworkSetForThisLesson?: 'Yes' | 'No';
  quizScore?: number;
  quizScoreOutOf?: number;
  overallStudentRating?: number;
  feedbackRewards?: string[];
  comments?: string;
  topicForNextClass?: string;
}

export interface IInitialLessonTutorFeedbacksState {
  status: 'idle' | 'loading' | 'rejected';
  lessonTutorFeedbackRewardsData?: ILessonTutorFeedbackRewardData;
  lessonTutorFeedbackListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ILessonTutorFeedback>;
  };
  // Admin
  adminLessonTutorFeedbackRewardsData?: ILessonTutorFeedbackRewardData;
  adminLessonTutorFeedbackListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ILessonTutorFeedback>;
  };
}

const initialState: IInitialLessonTutorFeedbacksState = {
  status: 'idle',
  lessonTutorFeedbackRewardsData: undefined,
  lessonTutorFeedbackListData: undefined,
  // ADMIN
  adminLessonTutorFeedbackRewardsData: undefined,
  adminLessonTutorFeedbackListData: undefined,
};

export const LessonTutorFeedbacksSlice = createSlice({
  initialState,
  name: 'lessonTutorFeedbacks',
  reducers: {
    resetLessonTutorFeedbackRewardsData: (state) => {
      state.lessonTutorFeedbackRewardsData = undefined;
    },
    resetLessonTutorFeedbackListData: (state) => {
      state.lessonTutorFeedbackListData = undefined;
    },
    addNewTutorFeedbackData: (
      state,
      action: PayloadAction<ILessonTutorFeedback[]>
    ) => {
      const result = action.payload;

      if (state.lessonTutorFeedbackListData) {
        const newLessonTutorFeedbackListData = {
          ...state.lessonTutorFeedbackListData,
        };

        if (result && result.length) {
          result.forEach((feedbackItem) => {
            newLessonTutorFeedbackListData.list.unshift(feedbackItem);

            newLessonTutorFeedbackListData.totalDocs =
              (newLessonTutorFeedbackListData.totalDocs || 0) + 1;
          });
        }

        state.lessonTutorFeedbackListData = newLessonTutorFeedbackListData;
      }
    },
    updateTutorFeedbackData: (
      state,
      action: PayloadAction<{ id: string; feedback: ILessonTutorFeedback }>
    ) => {
      const result = action.payload;

      if (state.lessonTutorFeedbackListData) {
        const newLessonTutorFeedbackListData = {
          ...state.lessonTutorFeedbackListData,
        };

        if (
          newLessonTutorFeedbackListData.list &&
          newLessonTutorFeedbackListData.list.length
        ) {
          const index = newLessonTutorFeedbackListData.list.findIndex(
            (item) => item._id === result.id
          );

          if (index > -1) {
            newLessonTutorFeedbackListData.list[index] = result.feedback;
          }
        }

        state.lessonTutorFeedbackListData = newLessonTutorFeedbackListData;
      }
    },
    // Admin
    resetAdminLessonTutorFeedbackRewardsData: (state) => {
      state.adminLessonTutorFeedbackRewardsData = undefined;
    },
    resetAdminLessonTutorFeedbackListData: (state) => {
      state.adminLessonTutorFeedbackListData = undefined;
    },
    adminAddNewTutorFeedbackData: (
      state,
      action: PayloadAction<ILessonTutorFeedback[]>
    ) => {
      const result = action.payload;

      if (state.adminLessonTutorFeedbackListData) {
        const newAdminLessonTutorFeedbackListData = {
          ...state.adminLessonTutorFeedbackListData,
        };

        if (result && result.length) {
          result.forEach((feedbackItem) => {
            newAdminLessonTutorFeedbackListData.list.unshift(feedbackItem);

            newAdminLessonTutorFeedbackListData.totalDocs =
              (newAdminLessonTutorFeedbackListData.totalDocs || 0) + 1;
          });
        }

        state.adminLessonTutorFeedbackListData = newAdminLessonTutorFeedbackListData;
      }
    },
    adminUpdateTutorFeedbackData: (
      state,
      action: PayloadAction<{ id: string; feedback: ILessonTutorFeedback }>
    ) => {
      const result = action.payload;

      if (state.adminLessonTutorFeedbackListData) {
        const newAdminLessonTutorFeedbackListData = {
          ...state.adminLessonTutorFeedbackListData,
        };

        if (
          newAdminLessonTutorFeedbackListData.list &&
          newAdminLessonTutorFeedbackListData.list.length
        ) {
          const index = newAdminLessonTutorFeedbackListData.list.findIndex(
            (item) => item._id === result.id
          );

          if (index > -1) {
            newAdminLessonTutorFeedbackListData.list[index] = result.feedback;
          }
        }

        state.adminLessonTutorFeedbackListData = newAdminLessonTutorFeedbackListData;
      }
    },
    adminDeleteTutorFeedbackData: (
      state,
      action: PayloadAction<{ id: string }>
    ) => {
      const result = action.payload;

      if (state.adminLessonTutorFeedbackListData) {
        const newAdminLessonTutorFeedbackListData = {
          ...state.adminLessonTutorFeedbackListData,
        };

        if (
          newAdminLessonTutorFeedbackListData.list &&
          newAdminLessonTutorFeedbackListData.list.length
        ) {
          newAdminLessonTutorFeedbackListData.list = newAdminLessonTutorFeedbackListData.list.filter(
            (item) => item._id !== result.id
          );

          if (newAdminLessonTutorFeedbackListData.totalDocs) {
            newAdminLessonTutorFeedbackListData.totalDocs -= 1;
          }
        }

        state.adminLessonTutorFeedbackListData = newAdminLessonTutorFeedbackListData;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getLessonTutorFeedbackRewards.fulfilled, (state, action) => {
        const result = action.payload;

        state.lessonTutorFeedbackRewardsData = result;
      })
      .addCase(getLessonTutorFeedbacks.fulfilled, (state, action) => {
        const result = action.payload;

        const newLessonTutorFeedbackListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.lessonTutorFeedbackListData = newLessonTutorFeedbackListData;
      });

    // Admin
    builder
      .addCase(
        adminGetLessonTutorFeedbackRewards.fulfilled,
        (state, action) => {
          const result = action.payload;

          state.adminLessonTutorFeedbackRewardsData = result;
        }
      )
      .addCase(adminGetLessonTutorFeedbacks.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminLessonTutorFeedbackListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminLessonTutorFeedbackListData = newAdminLessonTutorFeedbackListData;
      });

    // Common Matchers
    builder.addMatcher(
      isFulfilled(
        getLessonTutorFeedbackRewards,
        createLessonTutorFeedback,
        getLessonTutorFeedbacks,
        updateLessonTutorFeedbacks,
        // Admin
        adminGetLessonTutorFeedbackRewards,
        adminCreateLessonTutorFeedback,
        adminGetLessonTutorFeedbacks,
        adminUpdateLessonTutorFeedbacks,
        adminDeleteLessonTutorFeedback
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isPending(
        getLessonTutorFeedbackRewards,
        createLessonTutorFeedback,
        getLessonTutorFeedbacks,
        updateLessonTutorFeedbacks,
        // Admin
        adminGetLessonTutorFeedbackRewards,
        adminCreateLessonTutorFeedback,
        adminGetLessonTutorFeedbacks,
        adminUpdateLessonTutorFeedbacks,
        adminDeleteLessonTutorFeedback
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isRejected(
        getLessonTutorFeedbackRewards,
        createLessonTutorFeedback,
        getLessonTutorFeedbacks,
        updateLessonTutorFeedbacks,
        // Admin
        adminGetLessonTutorFeedbackRewards,
        adminCreateLessonTutorFeedback,
        adminGetLessonTutorFeedbacks,
        adminUpdateLessonTutorFeedbacks,
        adminDeleteLessonTutorFeedback
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getLessonTutorFeedbacksState = (
  state: RootState
): RootState['lessonTutorFeedbacks'] => state.lessonTutorFeedbacks;

export const {
  resetLessonTutorFeedbackRewardsData,
  resetLessonTutorFeedbackListData,
  addNewTutorFeedbackData,
  updateTutorFeedbackData,
  // Admin
  resetAdminLessonTutorFeedbackRewardsData,
  resetAdminLessonTutorFeedbackListData,
  adminAddNewTutorFeedbackData,
  adminUpdateTutorFeedbackData,
  adminDeleteTutorFeedbackData,
} = LessonTutorFeedbacksSlice.actions;

export * from './adminApi';
export * from './api';
export default LessonTutorFeedbacksSlice.reducer;
